import { TEAM_MANAGER_API_BASEURL } from "~app/constants";
import type { RequestConfig } from "./requestConfig";

/** Method to improve `fetch` and get better developer experience. Must be application agnostic. */
export const request = <TReturnValue>(
  endpoint: string,
  { body, onSuccess, onError, method, ...customConfig }: RequestConfig<TReturnValue> = {} as RequestConfig<TReturnValue>
): Promise<TReturnValue> => {
  const isFormData = body instanceof FormData;
  const isFile = body instanceof File;

  const config: RequestInit = {
    method: method ?? "GET",
    ...customConfig,
    headers: {
      ...(!isFormData && !isFile && { "Content-Type": "application/json" }),
      ...customConfig.headers,
    },
  };

  if (body) {
    config.body = isFormData || isFile ? body : JSON.stringify(body);
  }
  const url = endpoint.startsWith("http") ? endpoint : `${TEAM_MANAGER_API_BASEURL}${endpoint}`;

  return fetch(url, config).then(async (response) => {
    let data: TReturnValue;

    try {
      // handle empty success response
      if (response.ok && response.headers.get("Content-Type") == null) {
        return Promise.resolve() as Promise<TReturnValue>;
      }

      if (response.headers.get("Content-Type")?.includes("application/octet-stream")) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get("Content-Disposition");

        if (contentDisposition?.includes("filename=")) {
          const fileName = contentDisposition.split("filename=")[1].split(";")[0];
          data = new File([blob], fileName) as TReturnValue;
        } else {
          data = blob as TReturnValue;
        }
      } else if (
        response.headers.get("Content-Type")?.includes("text/plain") ||
        response.headers.get("Content-Type")?.includes("text/html")
      ) {
        data = (await response.text()) as TReturnValue;
      } else {
        data = await response.json();
      }
    } catch (e) {
      data = e as TReturnValue;
      console.error(e);
    }

    if (response.ok) {
      onSuccess?.(response, data);
      return data;
    }

    if (typeof onError === "function") {
      onError(response, data);
    } else {
      (onError?.[response.status] ?? onError?.default)?.(response, data);
    }

    return Promise.reject(data);
  });
};
